"use client";

import { useEffect, useRef, Suspense } from "react";
import { usePostHog } from "posthog-js/react";
import { useUser } from "@clerk/nextjs";

function IdentifyUser() {
  const { user, isLoaded } = useUser();
  const posthog = usePostHog();
  // Whether *this tab* has seen a signed-in user. Gates the `reset()` below so
  // it only ever runs on a real sign-out.
  const wasSignedIn = useRef(false);

  useEffect(() => {
    // Wait for Clerk to resolve — `useUser()` reports `user: undefined` while
    // loading, which is indistinguishable from "signed out" if you only check
    // `user`. See the reset() note below for why that matters.
    if (!isLoaded || !posthog) return;

    if (user) {
      // Identify the user if they are logged in.
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        phone: user.primaryPhoneNumber?.phoneNumber,
        username: user.username,
        image: user.imageUrl,
        fistName: user.firstName,
        lastName: user.lastName,
        lastSignInAt: user.lastSignInAt?.toISOString(),
        updatedAt: user.updatedAt?.toISOString(),
        createdAt: user.createdAt?.toISOString(),
        organizationMemberships: user.organizationMemberships.map(
          (membership) => ({
            organizationId: membership.organization.id,
            name: membership.organization.name,
          }),
        ),
      });
      console.debug("PostHog identified user", user.id);
      wasSignedIn.current = true;
      return;
    }

    // Signed out. `posthog.reset()` discards the distinct id and mints a fresh
    // anonymous one, so it must fire *only* on a genuine sign-out — never on a
    // first anonymous load, which is what the previous `else if (!user)` did on
    // every page load's pre-hydration window. Same fix as
    // `apps/app-dashboard/components/PostHogIdentifyUser.tsx`, where it was
    // severing the marketing → sign-up funnel outright.
    if (wasSignedIn.current) {
      posthog.reset();
      wasSignedIn.current = false;
    }
  }, [user, isLoaded, posthog]);

  return null;
}

// Wrap this in Suspense to avoid the `use******` usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
export default function PostHogIdentifyUser() {
  return (
    <Suspense fallback={null}>
      <IdentifyUser />
    </Suspense>
  );
}
PostHogIdentifyUser.displayName = "PostHogIdentifyUser";
